// covered-by: frontends/carrie/src/config/__tests__/askHostRedirect.test.ts
// why: the session JWT is per-origin localStorage — a hop to ask.puntt.ai without refreshedToken arrives logged out
// why: router.navigate throws on absolute URLs, so post-auth still targets /ask-puntt and location.replace leaves before V1 mounts
import { isAskSurface } from "./askSurface";
import { BASEPATH } from "./env";

const BRAND_ZONES = ["meaningfulgigs.com", "punttai.ai", "punttai.com"];

export type AskHostRedirectInput = {
  hostname: string;
  /** Browser pathname, including an epic base such as `/branch/ask-puntt`. */
  pathname: string;
  search?: string;
  /** With or without a leading `#`. */
  hash?: string;
  token?: string | null;
  basepath?: string;
};

type BrowserLoc = {
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
  replace: (url: string) => void;
};

type NavEvent = {
  toLocation: { pathname: string; searchStr: string; hash: string };
};

type AskRouter = {
  subscribe: (
    event: "onBeforeNavigate",
    fn: (event: NavEvent) => void,
  ) => () => void;
};

/**
 * Ask host that accompanies this tickets-app host. Mirrors
 * `services/christo/lib/libs/punttHosts.ts` `askSiblingHost`, plus the legacy
 * brand aliases on the same CloudFront distribution.
 */
export function askSiblingHost(hostname: string): string {
  const host = (hostname.split(":")[0] || "").toLowerCase();
  // why: Playwright still asserts the V1 tree on these hosts. Each Ask sibling mounts V2, and those specs cannot follow the hop.
  // Stryker disable next-line ConditionalExpression: e.puntt.ai matches no later app-host rule, so skipping this comparison still returns an empty sibling
  if (host === "e.puntt.ai") return "";
  if (host === "app-dev.puntt.ai" || host === "app-staging.puntt.ai") return "";
  if (host === "app.puntt.ai") return "ask.puntt.ai";
  if (host.startsWith("app-") && host.endsWith(".puntt.ai")) {
    return `ask-${host.slice("app-".length)}`;
  }
  for (const zone of BRAND_ZONES) {
    if (host === `app.${zone}`) return "ask.puntt.ai";
    const suffix = `.${zone}`;
    if (host.startsWith("app-") && host.endsWith(suffix)) {
      const stage = host.slice("app-".length, host.length - suffix.length);
      if (stage) return `ask-${stage}.puntt.ai`;
    }
  }
  return "";
}

function pathWithoutBase(pathname: string, basepath: string): string {
  const path = pathname.split("?")[0] || "/";
  const base = basepath.replace(/\/+$/, "");
  if (base && (path === base || path.startsWith(`${base}/`))) {
    return path.slice(base.length) || "/";
  }
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * `/ask-puntt` and descendants the Ask host can render.
 * `/ask-puntt-other` does not match. Campaigns stay on the app host: V2 has
 * no campaigns route, and the V1 page only redirects onward.
 */
export function isAppAskPunttPath(pathname: string, basepath = "/"): boolean {
  const path = pathWithoutBase(pathname, basepath);
  if (
    path === "/ask-puntt/campaigns" ||
    path.startsWith("/ask-puntt/campaigns/")
  ) {
    return false;
  }
  return path === "/ask-puntt" || path.startsWith("/ask-puntt/");
}

/**
 * V1 bookmarks a chat at `/ask-puntt/projects/:projectId/chats/:chatId` and a
 * canvas at `/ask-puntt/:chatId/canvas/:messageId`. V2 serves the chat at
 * `/ask-puntt/:chatId` and does not register canvas. Rewrite both on the way
 * across so the bookmark lands on that chat instead of the Ask host's not-found page.
 * `pathname` already has a leading slash; the caller adds one.
 */
export function v2AskPath(pathname: string): string {
  const projectChat = pathname.match(
    /^(.*)\/ask-puntt\/projects\/[^/]+\/chats\/([^/]+)\/?$/,
  );
  if (projectChat) {
    return `${projectChat[1]}/ask-puntt/${projectChat[2]}`;
  }
  const canvas = pathname.match(/^(.*)\/ask-puntt\/([^/]+)\/canvas\/[^/]+\/?$/);
  if (canvas) {
    return `${canvas[1]}/ask-puntt/${canvas[2]}`;
  }
  return pathname;
}

/** True when the token can be handed to the Ask origin. Expired tokens are dropped. */
export function sessionTokenHandoffReady(token: string): boolean {
  try {
    const b64 = `${token.split(".")[1]}`.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64)) as { exp?: unknown };
    // why: a missing exp is not proof the token is dead — derive() on the Ask origin still rejects a token it cannot use
    if (typeof json.exp !== "number") return true;
    return Date.now() / 1000 < json.exp;
  } catch {
    return false;
  }
}

/** Absolute Ask URL, or null when this navigation should stay on the app host. */
export function askHostRedirectUrl(input: AskHostRedirectInput): string | null {
  const sibling = askSiblingHost(input.hostname);
  if (!sibling) return null;
  const pathname = input.pathname.split("?")[0] || "/";
  if (!isAppAskPunttPath(pathname, input.basepath ?? "/")) return null;

  const askPath = v2AskPath(
    pathname.startsWith("/") ? pathname : `/${pathname}`,
  );
  const url = new URL(`https://${sibling}${askPath}`);
  const rawSearch = input.search ?? "";
  const params = new URLSearchParams(
    rawSearch.startsWith("?") ? rawSearch.slice(1) : rawSearch,
  );
  const token = input.token?.trim() ?? "";
  if (
    token &&
    !params.has("refreshedToken") &&
    sessionTokenHandoffReady(token)
  ) {
    params.set("refreshedToken", token);
  }
  const qs = params.toString();
  url.search = qs;
  const hashBody = (input.hash ?? "").replace(/^#/, "");
  url.hash = hashBody;
  return url.toString();
}

function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem("token");
  } catch {
    return null;
  }
}

/**
 * Full-page loads. Returns true after `location.replace` so the caller skips
 * rendering the V1 tree.
 */
export function redirectCurrentAppAskPuntt(
  loc: BrowserLoc = window.location,
  opts: { token?: string | null; basepath?: string } = {},
): boolean {
  // why: in-place V2 already lives on this origin so the session JWT stays put; the hop is only for the V1 tree
  if (isAskSurface()) return false;
  const url = askHostRedirectUrl({
    hostname: loc.hostname,
    pathname: loc.pathname,
    search: loc.search,
    hash: loc.hash,
    token: opts.token === undefined ? readStoredToken() : opts.token,
    basepath: opts.basepath ?? BASEPATH,
  });
  if (!url) return false;
  loc.replace(url);
  return true;
}

/**
 * Client navigations (`<Navigate to="/ask-puntt">`, sidebar links). TanStack
 * fires this before the V1 route loads; `location.replace` leaves the origin.
 */
export function installAskHostNavigationRedirect(
  router: AskRouter,
  read: () => {
    hostname: string;
    token: string | null;
    replace: (url: string) => void;
    basepath?: string;
  } = () => ({
    hostname: window.location.hostname,
    token: readStoredToken(),
    replace: (url: string) => window.location.replace(url),
    basepath: BASEPATH,
  }),
): void {
  router.subscribe("onBeforeNavigate", (event) => {
    // why: a client navigation on an already-mounted in-place V2 tree must not repeat the boot hop
    if (isAskSurface()) return;
    const current = read();
    const url = askHostRedirectUrl({
      hostname: current.hostname,
      pathname: event.toLocation.pathname,
      search: event.toLocation.searchStr,
      hash: event.toLocation.hash,
      token: current.token,
      basepath: current.basepath ?? BASEPATH,
    });
    if (url) current.replace(url);
  });
}
